import Guard from 'arg-guard'

import Logger from '@/logger'

import Renovation from '@/models/renovation'

import Service from './service'
import ServiceOperation from './service-operation'

/**
 * Gateway for accessing renovations data.
 * @export RenovationService
 * @class RenovationService
 */
export default class RenovationService extends Service {

    /**
     * Creates an instance of RenovationService.
     * @param {?number} schemeId The scheme for the active context.
     */
    constructor({ schemeId } = { schemeId: null }) {
        super('/renovation', 'Renovations')

        Guard.throwIf(schemeId, 'schemeId')
        this.schemeId = schemeId
    }

    /**
     * Build the URL path with a name.
     * @private
     * @param {number} addressId
     * @param {string} name
     * @returns {string}
     */
    _getUrlPath(addressId, name) {
        return `for-address/${ addressId }/${ name }`
    }

    /**
     * Loads the requested renovation.
     * @async
     * @param {number} addressId The address ID the renovation is linked to.
     * @param {string} name The name of the renovation.
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof RenovationService
     */
    async loadRenovationAsync(addressId, name) {
        const methodName = 'loadRenovationAsync'
        Logger.trackMethod(methodName, 'RenovationService', { addressId, name })
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(name, 'name')

        const operationState = new ServiceOperation('Load Renovation', true)
        const url = this._getUrlPath(addressId, name)
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [GET]')

        try {
            const response = await this.axios.get(url, this._buildAxiosConfig())
            return this._processData(response, operationState, methodName,
                (data) => new Renovation(data, addressId))
        } catch (err) {
            return this._failOperation(err, operationState, methodName)
        }
    }

    /**
     * Upserts the provided renovation data.
     * @async
     * @param {number} addressId The address ID the renovation is linked to.
     * @param {RenovationDataSubmission} data
     * @returns {Promise<ServiceOperation>} The operation result.
     * @memberof RenovationService
     */
    async upsertRenovationAsync(addressId, data) {
        const methodName = 'upsertRenovationAsync'
        Logger.trackMethod(methodName, 'RenovationService', { addressId, data, name })
        Guard.throwIf(addressId, 'addressId')
        Guard.throwIf(data, 'data')
        Guard.throwIf(data.name, 'data.name')

        data.schemeId = data.schemeId || this.schemeId
        const operationState = new ServiceOperation('Upsert Renovation', true)
        const url = this._getUrlPath(addressId, data.name)
        Logger.log(`${ this.baseUrl }/${ url }`, 'Request [PUT]')

        let statusCode = 200
        try {
            const response = await this.axios.put(url, data, this._buildAxiosConfig())
            // noinspection JSUnusedAssignment - Incorrect assesment
            statusCode = response.status
            return this._processResponse(response, operationState, methodName)
        } catch (err) {
            return statusCode !== 200
                ? this._failOperation(err, operationState, methodName, statusCode)
                : this._failOperation(err, operationState, methodName)
        }
    }
}
